import type { SchedulerBookingValue, SchedulerEmbedProvider } from "../types";
import { calcomEmbedNamespace } from "../utils";

type CalGlobal = {
  Cal?: {
    loaded?: boolean;
    ns?: Record<string, CalApi>;
    q?: unknown[];
    (instruction: string, ...args: unknown[]): void;
  };
};

type CalApi = ((instruction: string, ...args: unknown[]) => void) & { q?: unknown[] };

function ensureCalBootstrap(scriptUrl: string) {
  const w = window as Window & CalGlobal;
  if (w.Cal?.loaded) return;

  (function (C, A, L) {
    const p = function (a: CalApi, ar: unknown[]) {
      a.q?.push(ar);
    };
    const d = C.document;
    C.Cal =
      C.Cal ||
      function (this: CalApi, ...ar: unknown[]) {
        const cal = C.Cal as CalApi & { loaded?: boolean; ns?: Record<string, CalApi> };
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          const script = d.createElement("script");
          script.src = A;
          d.head.appendChild(script);
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function (this: CalApi, ...iar: unknown[]) {
            p(api, iar);
          } as CalApi;
          api.q = api.q || [];
          const namespace = ar[1];
          if (typeof namespace === "string") {
            cal.ns![namespace] = cal.ns![namespace] || api;
            p(cal.ns![namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else {
            p(cal, ar);
          }
          return;
        }
        p(cal, ar);
      };
  })(window as Window & CalGlobal, scriptUrl, "init");
}

function getCalApi(namespace: string): CalApi | null {
  const w = window as Window & CalGlobal;
  return w.Cal?.ns?.[namespace] ?? null;
}

function hasCalcomEmbedContent(slot: HTMLElement): boolean {
  return !!(
    slot.querySelector("iframe") ||
    slot.querySelector("cal-inline") ||
    slot.querySelector(".cal-embed")
  );
}

interface CalBookingSuccessPayload {
  uid?: string;
  startTime?: string;
  endTime?: string;
  videoCallUrl?: string;
}

interface CalEmbedEventDetail {
  type?: string;
  data?: { code?: string; msg?: string };
}

export const calcomEmbedProvider: SchedulerEmbedProvider = {
  id: "CALCOM",
  label: "Cal.com",

  mount(ctx) {
    let cancelled = false;
    let ready = false;
    const namespace = calcomEmbedNamespace(ctx.blockId);
    const slot = ctx.container;
    slot.innerHTML = "";
    slot.id = `cal-embed-${namespace}`;

    const markReady = () => {
      if (cancelled || ready) return;
      ready = true;
      ctx.onReady();
    };

    const markError = (message: string) => {
      if (cancelled || ready) return;
      ready = true;
      ctx.onReady();
      ctx.onError(message);
    };

    // Follow Cal.com's documented embed handshake: the bootstrap stub injects embed.js itself
    // (on the first `Cal(...)` call) and queues every instruction; embed.js then drains that
    // queue on load. We must NOT pre-load embed.js separately or pre-set `loaded`/`ns`/`q` —
    // doing so means embed.js loads against an empty queue, never installs its live-drain hook,
    // and our later `init`/`inline` calls pile up in a dead array (no iframe, 15s timeout).
    try {
      ensureCalBootstrap(ctx.embedConfig.widgetScriptUrl);
      const Cal = (window as Window & CalGlobal).Cal;
      if (!Cal) {
        markError("Cal.com embed failed to initialize.");
        return () => {
          cancelled = true;
          slot.innerHTML = "";
        };
      }

      // First Cal(...) call: the stub seeds ns/q, injects embed.js, and registers the namespace.
      Cal("init", namespace, { origin: "https://cal.com" });

      const cal = getCalApi(namespace);
      if (!cal) {
        markError("Cal.com embed failed to initialize.");
        return () => {
          cancelled = true;
          slot.innerHTML = "";
        };
      }

      const config: Record<string, string> = {};
      if (ctx.recalledName) config["name"] = ctx.recalledName;
      if (ctx.recalledEmail) config["email"] = ctx.recalledEmail;

      cal("on", {
        action: "bookerReady",
        callback: () => markReady(),
      });

      cal("on", {
        action: "linkReady",
        callback: () => markReady(),
      });

      cal("on", {
        action: "linkFailed",
        callback: (event: CustomEvent<CalEmbedEventDetail>) => {
          if (cancelled) return;
          const code = event.detail?.data?.code;
          const msg = event.detail?.data?.msg;
          markError(
            code === "404"
              ? "Cal.com event type not found — it may be unpublished or the booking link changed."
              : msg || "Cal.com booking widget couldn't load."
          );
        },
      });

      cal("on", {
        action: "bookingSuccessfulV2",
        callback: (event: CustomEvent<{ data?: CalBookingSuccessPayload }>) => {
          if (cancelled) return;
          const data = event.detail?.data;
          if (!data?.uid || !data.startTime || !data.endTime) return;
          const value: SchedulerBookingValue = {
            startTime: data.startTime,
            endTime: data.endTime,
            inviteeName: ctx.recalledName,
            inviteeEmail: ctx.recalledEmail,
            externalId: data.uid,
            externalUrl: `https://cal.com/booking/${data.uid}`,
            meetUrl: data.videoCallUrl ?? null,
          };
          ctx.onBooking(value);
        },
      });

      cal("inline", {
        elementOrSelector: slot,
        calLink: ctx.embedConfig.embedUrl,
        config,
      });

      window.setTimeout(() => {
        if (cancelled || ready) return;
        if (hasCalcomEmbedContent(slot)) {
          markReady();
          return;
        }
        markError(
          "Cal.com booking widget couldn't load. Check that your event type is published and your Cal.com username is set."
        );
      }, 15_000);
    } catch (e) {
      markError(e instanceof Error ? e.message : "Cal.com embed failed to initialize.");
    }

    return () => {
      cancelled = true;
      slot.innerHTML = "";
    };
  },
};
